import { AlignmentTransform } from "@/modes/alignment-modes-commons/alignment-transform";
import { TwoPointsPairsAlignmentAnchorPositions } from "@/modes/alignment-modes-commons/compute-split-screen-alignment";
import {
  AlignmentViewLayout,
  SplitDirection,
} from "@/store/modes/alignment-ui-types";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Vector3Tuple } from "three";

/* reference that is used for sheet and cad alignment, to support both sheet-cad and cad-to-sheet alignment */
export enum SheetCadAlignReference {
  // align sheet to cad
  bimModel = "bimModel",

  // align cad to sheet
  sheet = "sheet",
}

/**
 * This defines all the steps to align sheet and a 3D model
 * IMPORTANT: The order of definition matters. It's used as index
 * and to determine what is the previous or next step.
 */
export enum SheetCadAlignmentStep {
  setElevation = "setElevation",
  alignIn2D = "alignIn2D",
}

/** State managing data exchange during sheet and cad alignment session */
type SheetCadAlignmentToolState = {
  /** reference type for sheet and cad alignment */
  alignmentReference: SheetCadAlignReference;

  /** reference to sheet ID used during current alignment session */
  sheetIdForAlignment?: string;

  /** Alignment step */
  step: SheetCadAlignmentStep;

  /** 2D Alignment screen layout */
  alignmentLayout: AlignmentViewLayout;

  /** 2D alignment screen split direction */
  splitDirection: SplitDirection;

  /** if true clipping box in Vertical alignment step is enabled */
  isClippingBoxEnabled: boolean;

  /** if true, extract cross section at sheet elevation for cad model tomographic view */
  isCrossSectionEnabled: boolean;

  /**
   * Elevation of sheet plane relatively to CAD origin in W.C.S., in meters
   * The value will be initialized in mode transition to the value of the current sheet elevation.
   * The value will be updated from TransformControls widget in Alignment Tool UI
   */
  sheetElevation: number;

  /**
   * Incremental transform that will align the sheet/area in its current world to CAD model space (sheet-to-cad)
   * or vice versa (cad-to-sheet). Translations are in meters.
   * At the end of each alignment cycle, it must be reset to undefined to prevent reusing it in next session of alignment
   */
  incrementalTransform?: AlignmentTransform;

  /** anchor positions defined in split screen alignment */
  alignmentAnchorPositions: TwoPointsPairsAlignmentAnchorPositions;
};

const initialState: SheetCadAlignmentToolState = {
  alignmentReference: SheetCadAlignReference.bimModel,
  step: SheetCadAlignmentStep.setElevation,
  alignmentLayout: AlignmentViewLayout.splitScreen,
  splitDirection: SplitDirection.horizontalSplit,
  alignmentAnchorPositions: {},
  isClippingBoxEnabled: false,
  isCrossSectionEnabled: true,
  sheetElevation: 0,
};

const sheetCadAlignmentModeSlice = createSlice({
  initialState,
  name: "sheetCadAlignmentMode",
  reducers: {
    setSheetCadAlignmentReference(
      state,
      action: PayloadAction<SheetCadAlignReference>,
    ) {
      state.alignmentReference = action.payload;
    },

    setSheetForSheetCadAlignment(state, action: PayloadAction<string>) {
      state.sheetIdForAlignment = action.payload;
    },

    setSheetCadAlignmentStep(
      state,
      action: PayloadAction<SheetCadAlignmentStep>,
    ) {
      state.step = action.payload;
    },

    setSheetCadAlignmentLayout(
      state,
      action: PayloadAction<AlignmentViewLayout>,
    ) {
      state.alignmentLayout = action.payload;
    },

    setSheetCadAlignmentSplitDirection(
      state,
      action: PayloadAction<SplitDirection>,
    ) {
      state.splitDirection = action.payload;
    },

    setClippingBoxEnabled(state, action: PayloadAction<boolean>) {
      state.isClippingBoxEnabled = action.payload;
    },

    setSheetCadIncrementalTransform(
      state,
      action: PayloadAction<AlignmentTransform | undefined>,
    ) {
      state.incrementalTransform = action.payload;
    },

    setSheetElevation(state, action: PayloadAction<number>) {
      state.sheetElevation = action.payload;
    },

    setCrossSectionEnabled(state, action: PayloadAction<boolean>) {
      state.isCrossSectionEnabled = action.payload;
    },

    resetSheetCadAlignment(state) {
      state.incrementalTransform = undefined;
      state.sheetIdForAlignment = undefined;
      state.alignmentAnchorPositions = {};
    },

    setReferenceElementAnchor1(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor1 = action.payload;
    },

    setReferenceElementAnchor2(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor2 = action.payload;
    },

    setMovingElementAnchor1(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor1 = action.payload;
    },

    setMovingElementAnchor2(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor2 = action.payload;
    },
  },
});

export const {
  setSheetCadAlignmentReference,
  setSheetForSheetCadAlignment,
  setSheetCadAlignmentStep,
  setSheetCadAlignmentLayout,
  setSheetCadAlignmentSplitDirection,
  setClippingBoxEnabled,
  setSheetCadIncrementalTransform,
  setSheetElevation,
  resetSheetCadAlignment,
  setMovingElementAnchor1,
  setMovingElementAnchor2,
  setReferenceElementAnchor1,
  setReferenceElementAnchor2,
  setCrossSectionEnabled,
} = sheetCadAlignmentModeSlice.actions;

export const sheetCadAlignmentModeReducer = sheetCadAlignmentModeSlice.reducer;
